import type { CSSProperties, ElementType, ReactNode } from "react";

import { HandyoutPaletteStyle } from "@/components/handyout-palette-style";
import type { PaletteScheme } from "@/lib/handyout-palette";

export const HANDYOUT_FALLBACK_PRIMARY = "#E8672E";

/**
 * Scopes the author palette to one rendered handyout.
 *
 * With a scheme, the wrapper carries `data-handyout-palette` and the computed
 * light and dark tokens arrive through a scoped `<style>` rule. Without one,
 * `--primary` is pinned inline. The pin must be absent whenever a scheme is
 * active, because an inline declaration beats the scoped rule and cannot hold
 * a light and a dark value at once.
 *
 * `scopeId` has to be unique per rendered handyout on the page, so grids pass
 * the handyout id and static demos a fixed id.
 */
export function HandyoutPaletteScope({
  primaryColor,
  paletteScheme,
  scopeId,
  className,
  style,
  as,
  followTheme,
  children,
}: {
  primaryColor?: string | null;
  paletteScheme?: PaletteScheme | null;
  scopeId?: string | null;
  className?: string;
  style?: CSSProperties;
  as?: ElementType;
  followTheme?: boolean;
  children?: ReactNode;
}) {
  const Component: ElementType = as ?? "div";
  const primary = primaryColor || HANDYOUT_FALLBACK_PRIMARY;
  const activeScopeId = paletteScheme && scopeId ? scopeId : null;

  const mergedStyle: CSSProperties | undefined = activeScopeId
    ? style
    : ({ ...style, "--primary": primary } as CSSProperties);

  return (
    <Component
      data-handyout-scope=""
      data-handyout-theme={followTheme ? "follow" : undefined}
      data-handyout-palette={activeScopeId ?? undefined}
      className={className}
      style={mergedStyle}
    >
      <HandyoutPaletteStyle
        primary={primary}
        scheme={paletteScheme}
        scopeId={activeScopeId}
      />
      {children}
    </Component>
  );
}

export function handyoutPaletteProps(
  handyout:
    | {
        id?: string | null;
        primary_color?: string | null;
        palette_scheme?: PaletteScheme | string | null;
      }
    | null
    | undefined,
): {
  primaryColor: string;
  paletteScheme: PaletteScheme | null;
  scopeId: string | null;
} {
  return {
    primaryColor: handyout?.primary_color || HANDYOUT_FALLBACK_PRIMARY,
    paletteScheme: (handyout?.palette_scheme as PaletteScheme | null) ?? null,
    scopeId: handyout?.id ? String(handyout.id) : null,
  };
}
